import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Search, Bot, User, Clock, Send, ArrowRight, X, MessageSquare, ExternalLink, Wifi } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/atendimento`;
const headers = () => ({Authorization: `Bearer ${localStorage.getItem('recupnet_token')}`});

const STATUS_MAP = {
  open: {label:'Aberto', badge:'badge-neutral'},
  waiting_ai: {label:'IA', badge:'badge-success'},
  waiting_human: {label:'Fila Humano', badge:'badge-warning'},
  in_progress: {label:'Em Atendimento', badge:'badge-success'},
  on_hold: {label:'Em Espera', badge:'badge-neutral'},
  closed: {label:'Encerrado', badge:'badge-neutral'},
};

export default function AtendimentoInbox() {
  const navigate = useNavigate();
  const [convs, setConvs] = useState([]);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('');
  const [newMsg, setNewMsg] = useState('');
  const [sending, setSending] = useState(false);
  const [syncStatus, setSyncStatus] = useState('online');
  const [lastSync, setLastSync] = useState(null);
  const pollConvsRef = useRef(null);
  const pollMsgsRef = useRef(null);
  const msgsEndRef = useRef(null);
  const searchRef = useRef(search);
  const filterRef = useRef(filter);
  const selectedRef = useRef(selected);

  // Keep refs in sync
  useEffect(() => { searchRef.current = search; }, [search]);
  useEffect(() => { filterRef.current = filter; }, [filter]);
  useEffect(() => { selectedRef.current = selected; }, [selected]);

  // Fetch conversations (non-destructive: preserves state)
  const fetchConvs = useCallback(async (silent = false) => {
    try {
      const params = { limit: 50 };
      if (searchRef.current) params.search = searchRef.current;
      if (filterRef.current) params.status = filterRef.current;
      const r = await axios.get(`${API}/conversations`, { headers: headers(), params });
      setConvs(r.data.conversations || []);
      setSyncStatus('online');
      setLastSync(new Date());
    } catch {
      setSyncStatus('error');
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  // Fetch messages for open ticket (incremental)
  const fetchMessages = useCallback(async () => {
    if (!selectedRef.current) return;
    try {
      const r = await axios.get(`${API}/conversations/${selectedRef.current}`, { headers: headers() });
      setDetail(prev => {
        if (!prev) return r.data;
        // Only update if message count changed (avoid flicker)
        const newCount = r.data.messages?.length || 0;
        const oldCount = prev.messages?.length || 0;
        if (newCount !== oldCount) {
          return r.data;
        }
        // Update conversation status without touching messages
        return { ...prev, conversation: r.data.conversation, contact: r.data.contact, recupLink: r.data.recupLink };
      });
    } catch {}
  }, []);

  // Initial load
  useEffect(() => { fetchConvs(); }, []);
  // Re-fetch on filter/search change
  useEffect(() => { fetchConvs(true); }, [search, filter]);

  // Polling: conversations every 4s
  useEffect(() => {
    pollConvsRef.current = setInterval(() => fetchConvs(true), 4000);
    return () => clearInterval(pollConvsRef.current);
  }, [fetchConvs]);

  // Polling: messages every 3s when ticket is open
  useEffect(() => {
    if (selected) {
      fetchMessages();
      pollMsgsRef.current = setInterval(fetchMessages, 3000);
    }
    return () => { if (pollMsgsRef.current) clearInterval(pollMsgsRef.current); };
  }, [selected, fetchMessages]);

  // Smart auto-scroll: only if user is near bottom
  useEffect(() => {
    if (msgsEndRef.current) {
      const container = msgsEndRef.current.parentElement;
      if (container) {
        const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 100;
        if (isNearBottom) {
          msgsEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
      }
    }
  }, [detail?.messages?.length]);

  const openConv = (id) => {
    setSelected(id);
    axios.get(`${API}/conversations/${id}`, { headers: headers() }).then(r => setDetail(r.data));
  };

  const sendMessage = async () => {
    if (!newMsg.trim() || !selected) return;
    setSending(true);
    try {
      await axios.post(`${API}/conversations/${selected}/messages`, { content: newMsg, sender: 'operator', senderName: 'Operador' }, { headers: headers() });
      setNewMsg('');
      // Immediate refresh
      const r = await axios.get(`${API}/conversations/${selected}`, { headers: headers() });
      setDetail(r.data);
    } catch {} finally { setSending(false); }
  };

  const transfer = async (target) => {
    if (!selected) return;
    await axios.post(`${API}/conversations/${selected}/transfer`, { target }, { headers: headers() });
    fetchMessages();
    fetchConvs(true);
  };

  const closeConv = async () => {
    if (!selected) return;
    await axios.post(`${API}/conversations/${selected}/close`, {}, { headers: headers() });
    setSelected(null); setDetail(null);
    fetchConvs(true);
  };

  return (
    <div className="flex h-screen" style={{maxHeight:'100vh'}}>
      {/* LEFT: Conversation List */}
      <div className="w-[320px] border-r border-white/10 flex flex-col flex-shrink-0" style={{background:'rgba(11,15,16,0.6)'}}>
        <div className="p-3 border-b border-white/10 space-y-2">
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30"/>
              <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Buscar..."
                className="w-full bg-black/20 border border-white/10 text-white text-xs placeholder:text-white/25 rounded-xl pl-9 pr-3 py-2 focus:outline-none focus:ring-1 focus:ring-[rgba(99,203,182,0.3)]"/>
            </div>
            <div className="flex items-center gap-1" title={lastSync ? `Atualizado ${lastSync.toLocaleTimeString('pt-BR')}` : ''}>
              <div className={`w-1.5 h-1.5 rounded-full ${syncStatus === 'online' ? 'bg-[var(--rn-green)]' : syncStatus === 'error' ? 'bg-red-400' : 'bg-yellow-400'}`}/>
              <span className="text-[8px] text-white/20">{syncStatus === 'online' ? 'Live' : 'Off'}</span>
            </div>
          </div>
          <div className="flex gap-1 flex-wrap">
            {['','waiting_ai','waiting_human','in_progress','closed'].map(s => (
              <button key={s} onClick={()=>setFilter(s)}
                className={`text-[9px] px-2 py-1 rounded-full ${filter===s ? 'bg-[var(--rn-green)] text-black' : 'bg-white/5 text-white/40 hover:bg-white/10'}`}>
                {s ? (STATUS_MAP[s]?.label || s) : 'Todos'}
              </button>
            ))}
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          {loading ? <div className="flex justify-center py-8"><div className="rn-spinner" style={{width:18,height:18}}/></div> :
           convs.length === 0 ? <p className="text-xs text-white/25 text-center py-8">Nenhuma conversa</p> :
           convs.map(c => (
            <button key={c.id} onClick={()=>openConv(c.id)}
              className={`w-full text-left p-3 border-b border-white/5 transition-colors ${selected===c.id ? 'bg-[rgba(99,203,182,0.06)]' : 'hover:bg-white/[0.03]'}`}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-medium text-white/85 truncate max-w-[160px]">{c.contactName}</span>
                <span className={`${STATUS_MAP[c.status]?.badge || 'badge-neutral'} text-[8px]`}>{STATUS_MAP[c.status]?.label || c.status}</span>
              </div>
              <div className="flex items-center gap-1.5 mb-1">
                {c.currentOwner === 'ai' ? <Bot size={10} style={{color:'var(--rn-green)'}}/> : c.currentOwner === 'human' ? <User size={10} className="text-white/40"/> : <Clock size={10} className="text-white/20"/>}
                <span className="text-[9px] text-white/30">{c.currentOwner === 'ai' ? 'IA' : c.operatorName || 'Sem operador'}</span>
                {c.priority === 'urgent' && <span className="badge-danger text-[7px]">URGENTE</span>}
                {c.priority === 'high' && <span className="badge-warning text-[7px]">ALTA</span>}
              </div>
              <p className="text-[10px] text-white/30 truncate">{c.lastMessagePreview}</p>
            </button>
          ))}
        </div>
      </div>

      {/* CENTER: Chat */}
      {!detail ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center"><MessageSquare size={48} className="mx-auto text-white/10 mb-2"/><p className="text-xs text-white/25">Selecione uma conversa</p></div>
        </div>
      ) : (
        <div className="flex-1 flex flex-col">
          {/* Chat header */}
          <div className="p-3 border-b border-white/10 flex items-center justify-between" style={{background:'rgba(15,20,24,0.8)'}}>
            <div>
              <h3 className="text-sm font-semibold">{detail.conversation?.contactName}</h3>
              <p className="text-[10px] text-white/35">{detail.conversation?.contactPhone} · {detail.conversation?.protocol}</p>
            </div>
            <div className="flex items-center gap-1.5">
              <button onClick={() => navigate(`/admin/atendimento/ticket/${selected}`)} className="text-[9px] px-2.5 py-1 rounded-lg bg-white/5 text-white/50 hover:bg-white/10 flex items-center gap-1"><ExternalLink size={10}/> Ticket</button>
              {detail.conversation?.currentOwner !== 'ai' && (
                <button onClick={()=>transfer('ai')} className="text-[9px] px-2.5 py-1 rounded-lg bg-white/5 text-white/50 hover:bg-white/10">Retornar p/ IA</button>
              )}
              {detail.conversation?.currentOwner !== 'human' && (
                <button onClick={()=>transfer('human')} className="text-[9px] px-2.5 py-1 rounded-lg bg-white/5 text-white/50 hover:bg-white/10">Assumir</button>
              )}
              <button onClick={closeConv} className="text-[9px] px-2.5 py-1 rounded-lg bg-white/5 text-red-400/60 hover:bg-red-500/10">Encerrar</button>
            </div>
          </div>
          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3" style={{background:'rgba(11,15,16,0.4)'}}>
            {detail.messages?.map(m => (
              <div key={m.id} className={`flex ${m.sender === 'client' ? 'justify-start' : 'justify-end'}`}>
                <div className={`max-w-[70%] rounded-2xl px-3.5 py-2.5 ${m.type === 'system' ? 'bg-white/5 text-white/30 text-center mx-auto text-[10px] italic max-w-full' : m.sender === 'client' ? 'bg-white/[0.08] text-white/85' : m.isAI ? 'bg-[rgba(99,203,182,0.12)] text-white/85' : 'bg-[rgba(99,203,182,0.2)] text-white/90'}`}>
                  {m.type !== 'system' && <p className="text-[9px] text-white/35 mb-0.5">{m.isAI ? '🤖 ' : ''}{m.senderName}</p>}
                  <p className="text-xs leading-relaxed">{m.content}</p>
                  <p className="text-[8px] text-white/20 mt-1">{m.createdAt ? new Date(m.createdAt).toLocaleTimeString('pt-BR',{hour:'2-digit',minute:'2-digit'}) : ''}</p>
                </div>
              </div>
            ))}
            <div ref={msgsEndRef}/>
          </div>
          <div className="p-3 border-t border-white/10 flex gap-2" style={{background:'rgba(15,20,24,0.8)'}}>
            <input value={newMsg} onChange={e=>setNewMsg(e.target.value)} onKeyDown={e=>e.key==='Enter'&&sendMessage()}
              placeholder="Digite sua mensagem..."
              className="flex-1 bg-black/20 border border-white/10 text-white text-xs placeholder:text-white/25 rounded-xl px-4 py-2.5 focus:outline-none focus:ring-1 focus:ring-[rgba(99,203,182,0.3)]"/>
            <button onClick={sendMessage} disabled={sending || !newMsg.trim()}
              className="btn-primary !py-2 !px-4 text-xs disabled:opacity-30">
              <Send size={14}/>
            </button>
          </div>
        </div>
      )}

      {/* RIGHT: Contact Panel */}
      {detail?.contact && (
        <div className="w-[260px] border-l border-white/10 p-4 overflow-y-auto flex-shrink-0" style={{background:'rgba(11,15,16,0.6)'}}>
          <h4 className="text-xs font-semibold text-white/60 mb-3">Dados do Cliente</h4>
          <div className="space-y-2 text-xs">
            <div><span className="text-white/30">Nome:</span> <span className="text-white/80">{detail.contact.name}</span></div>
            <div><span className="text-white/30">Telefone:</span> <span className="text-white/80 font-mono">{detail.contact.phone}</span></div>
            {detail.contact.cpf && <div><span className="text-white/30">CPF:</span> <span className="text-white/80 font-mono">{detail.contact.cpf}</span></div>}
            <div><span className="text-white/30">Canal:</span> <span className="text-white/80">{detail.contact.origin}</span></div>
            <div><span className="text-white/30">Atendimentos:</span> <span className="text-white/80">{detail.contact.totalConversations}</span></div>
          </div>
          {detail.contact.tags?.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1">
              {detail.contact.tags.map(t => <span key={t} className="badge-neutral text-[8px]">{t}</span>)}
            </div>
          )}
          <div className="mt-4 pt-3 border-t border-white/10">
            <h4 className="text-xs font-semibold text-white/60 mb-2">Conversa</h4>
            <div className="space-y-1 text-[10px] text-white/35">
              <p>Fila: {detail.conversation?.queueName}</p>
              <p>Protocolo: {detail.conversation?.protocol}</p>
              <p>Mensagens: {detail.conversation?.messageCount}</p>
              <p>Responsavel: {detail.conversation?.currentOwner === 'ai' ? 'IA' : detail.conversation?.operatorName || '—'}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
